import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Building2, 
  Calculator, 
  FileText, 
  TrendingUp, 
  CheckCircle2, 
  ArrowRight, 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff,
  Shield,
  BarChart3,
  FileCheck,
  AlertCircle,
  Settings,
  Lightbulb,
  Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axiosInstance, { setAuthToken } from '@/lib/axios';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState('standard'); // 'standard', 'register', 'vip'
  const [loading, setLoading] = useState(false);
  
  // VIP login
  const [vipEmail, setVipEmail] = useState('');
  
  // Standard login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Registration
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerFirstName, setRegisterFirstName] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  
  // Referral code
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralValid, setReferralValid] = useState(null);
  const [referralReferrer, setReferralReferrer] = useState('');
  
  // Registration validation errors (French inline messages)
  const [registerErrors, setRegisterErrors] = useState({});
  
  // CGV/CGU modals
  const [showCGV, setShowCGV] = useState(false);
  const [showCGU, setShowCGU] = useState(false);
  
  // USP Popup (show on first visit)
  const [showUSPPopup, setShowUSPPopup] = useState(false);
  
  // PWA Install Prompt (show AFTER USP popup is closed)
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  
  // Check if should show USP popup on mount
  React.useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('hasSeenUSPPopup');
    if (!hasSeenPopup) {
      // Show popup after 1.5 seconds
      const timer = setTimeout(() => {
        setShowUSPPopup(true);
        sessionStorage.setItem('hasSeenUSPPopup', 'true');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle USP popup close - then show PWA prompt
  const handleUSPPopupClose = (startNow = false) => {
    setShowUSPPopup(false);
    if (startNow) {
      setShowLoginModal(true);
      setLoginMode('register');
    }
    // Show PWA install prompt after a short delay
    const pwaTimer = setTimeout(() => {
      const hasDismissedPWA = sessionStorage.getItem('pwaPromptDismissed');
      if (!hasDismissedPWA) {
        setShowPWAPrompt(true);
      }
    }, 1500);
    return () => clearTimeout(pwaTimer);
  };

  // Validate referral code when it changes
  React.useEffect(() => {
    const validateCode = async () => {
      if (referralCode && referralCode.length >= 6) {
        try {
          const response = await axiosInstance.get(`${API}/referral/validate/${referralCode}`);
          if (response.data.valid) {
            setReferralValid(true);
            setReferralReferrer(response.data.referrer_name);
          } else {
            setReferralValid(false);
            setReferralReferrer('');
          }
        } catch (error) {
          setReferralValid(false);
          setReferralReferrer('');
        }
      } else {
        setReferralValid(null);
        setReferralReferrer('');
      }
    };
    validateCode();
  }, [referralCode]);

  // VIP passwordless login
  const handleVipLogin = async (e) => {
    e.preventDefault();
    if (!vipEmail) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/auth/login`, {
        email: vipEmail
      }, { withCredentials: true });

      // Store session token in localStorage for Safari compatibility
      if (response.data.session_token) {
        setAuthToken(response.data.session_token);
      }

      toast.success(response.data.message || 'Connexion réussie');
      navigate('/dashboard', { 
        state: { 
          user: response.data.user,
          session_token: response.data.session_token 
        }, 
        replace: true 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email non reconnu');
    } finally {
      setLoading(false);
    }
  };

  // Standard login with password
  const handleStandardLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/auth/login-password`, {
        email: loginEmail,
        password: loginPassword,
        name: '',
        first_name: ''
      }, { withCredentials: true });  // This sets the httpOnly cookie

      // Store session token in localStorage for Safari compatibility
      if (response.data.session_token) {
        setAuthToken(response.data.session_token);
        console.log('[Login] Token stored in localStorage');
      }

      toast.success(response.data.message || 'Connexion réussie');
      
      // Simple redirect - the cookie is already set by the backend
      // ProtectedRoute will verify with /api/auth/me
      window.location.href = '/dashboard';
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';
      toast.error(errorDetail || 'Email ou mot de passe incorrect');
      setLoading(false);
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Registration validation function
  const validateRegistration = () => {
    const errors = {};
    
    if (!registerFirstName.trim()) {
      errors.firstName = 'Veuillez entrer votre prénom';
    }
    
    if (!registerName.trim()) {
      errors.name = 'Veuillez entrer votre nom';
    }
    
    if (!registerEmail.trim()) {
      errors.email = 'Veuillez entrer votre adresse e-mail';
    } else if (!isValidEmail(registerEmail)) {
      errors.email = 'Veuillez entrer une adresse e-mail valide';
    }
    
    if (!registerPassword) {
      errors.password = 'Veuillez entrer un mot de passe';
    } else if (registerPassword.length < 6) {
      errors.password = 'Le mot de passe doit faire au moins 6 caractères';
    }
    
    if (!registerPasswordConfirm) {
      errors.passwordConfirm = 'Veuillez confirmer votre mot de passe';
    } else if (registerPassword !== registerPasswordConfirm) {
      errors.passwordConfirm = 'Les mots de passe ne correspondent pas';
    }
    
    setRegisterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Registration
  // Registration
  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Run custom validation
    if (!validateRegistration()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/auth/register`, {
        email: registerEmail,
        password: registerPassword,
        name: registerName,
        first_name: registerFirstName
      }, { withCredentials: true });

      const { session_token, user } = response.data;
      
      // Store token
      if (session_token) {
        setAuthToken(session_token);
        console.log('[Register] Token stored');
      }

      // Apply referral code if valid
      if (referralCode && referralValid) {
        try {
          await axiosInstance.post(`${API}/referral/apply`, {
            referral_code: referralCode
          }, { withCredentials: true });
          toast.success('Code de parrainage appliqué ! +1 simulation gratuite');
        } catch (refError) {
          console.log('Referral apply error:', refError);
        }
      }

      toast.success(response.data.message || 'Compte créé');
      
      // Simple redirect - cookie already set
      window.location.href = '/dashboard';
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
      setLoading(false);
    }
  };

  // Google OAuth login
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    "Analyse automatique location courte durée (Airbnb) - données réelles",
    "Analyse automatique location longue durée - données INSEE",
    "Calcul automatisé du cash-flow avec recommandation",
    "Simulation de la « charge mentale » Airbnb (indicateur graphique)",
    "Scénarios entièrement paramétrables (jours de location LMNP)",
    "Charges simulées paramétrables",
    "Fiscalité simulée (RFR/parts)",
    "Simulation du prix de revente selon l'activité exercée",
    "Calcul de la plus-value de sortie (taxe PV immobilière incluse)",
    "Export PDF professionnel pour la banque"
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Simulateur Rentabilité Locative Gratuit 2025 | Achat Immo Gagnant</title>
        <meta name="description" content="Calculez gratuitement la rentabilité de votre investissement locatif. Simulateur complet avec cash-flow, rendement net, comparaison Airbnb vs location traditionnelle. Génération de dossier bancaire." />
        <meta name="keywords" content="simulateur rentabilité locative, calcul rentabilité locative, simulateur investissement immobilier, rentabilité locative gratuit, cash flow immobilier, tableau calcul rentabilité, calcul rentabilité investissement" />
        <meta property="og:title" content="Simulateur Rentabilité Locative - Achat Immo Gagnant" />
        <meta property="og:description" content="Calculez la rentabilité de votre investissement immobilier locatif. Comparez Airbnb et location traditionnelle." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://achat-immo-gagnant.fr" />
      </Helmet>
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-blue-600" />
              <h1 className="text-xl font-bold text-slate-800">
                Achat Immo Gagnant
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => { setShowLoginModal(true); setLoginMode('standard'); }}
                data-testid="login-button"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Connexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Analysez la Rentabilité de Votre Investissement Immobilier
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Outil professionnel pour calculer le rendement de vos investissements locatifs et constituer votre dossier bancaire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => { setShowLoginModal(true); setLoginMode('register'); }}
                data-testid="get-started-button"
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8"
              >
                Commencer gratuitement
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Complete Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-slate-800 mb-4">
              Une Analyse Complète de Votre Investissement
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              De l'étude de marché à la stratégie de sortie, tout ce qu'il faut pour convaincre votre banquier.
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            {/* Grid of all features - Organized by investor journey */}
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* 1. Étude de Marché - Premier besoin */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Étape 1</div>
                    <h4 className="text-lg font-bold text-slate-800 mb-3">
                      Étude de Marché Automatisée
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Analyse des prix <strong>Airbnb en temps réel</strong> sur votre zone (données scrapées)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Analyse du marché locatif traditionnel (<strong>données INSEE officielles</strong>)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Comparatif automatique des revenus potentiels</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 2. Simulation Financière */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calculator className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Étape 2</div>
                    <h4 className="text-lg font-bold text-slate-800 mb-3">
                      Simulation Financière Complète
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Calcul du <strong>cash-flow mensuel et annuel</strong> selon le type de location</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Charges entièrement paramétrables</strong> (taxe foncière, assurance, gestion...)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Scénarios LMNP personnalisables (nombre de jours de location)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 3. Fiscalité */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border border-indigo-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileCheck className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-1">Étape 3</div>
                    <h4 className="text-lg font-bold text-slate-800 mb-3">
                      Simulation Fiscale Personnalisée
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Calcul basé sur votre <strong>RFR et nombre de parts fiscales</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Comparaison des régimes <strong>micro-BIC vs réel simplifié</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Impact fiscal réel sur votre imposition</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 4. Stratégie de Sortie */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Étape 4</div>
                    <h4 className="text-lg font-bold text-slate-800 mb-3">
                      Stratégie de Sortie & Plus-Value
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Estimation du prix de revente</strong> selon l'activité exercée</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Calcul de la <strong>plus-value nette</strong> (taxe PV immobilière incluse)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Abattements selon durée de détention, exception résidence principale</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 5. Recommandation & Aide à la Décision */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">Étape 5</div>
                    <h4 className="text-lg font-bold text-slate-800 mb-3">
                      Recommandation & Aide à la Décision
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Recommandation personnalisée</strong> : location longue durée ou LMNP ?</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Comparatif chiffré des deux stratégies</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Indicateur de "charge de gestion" pour chaque option</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 6. Dossier Bancaire - Livrable final */}
              <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl p-6 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="h-6 w-6 text-slate-800" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-1">Livrable</div>
                    <h4 className="text-lg font-bold text-white mb-3">
                      Dossier PDF Professionnel
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                        <span><strong>Export PDF complet</strong> prêt pour votre banquier</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                        <span>Synthèse executive, graphiques et projections</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                        <span>Données de marché <strong>sourcées et vérifiables</strong></span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-slate-800 mb-4">
                Simulation Complète d'Investissement
              </h3>
              <p className="text-lg text-slate-600">
                Constituez votre dossier professionnel pour votre demande de financement bancaire.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-4">
                      <FileText className="h-6 w-6 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">
                        Simulation + Dossier PDF
                      </span>
                    </div>
                    <h4 className="text-2xl font-bold text-slate-800 mb-4">
                      Analyse Complète de Rentabilité
                    </h4>
                    <ul className="space-y-3">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3 text-slate-600">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="md:text-center md:border-l md:border-slate-200 md:pl-12">
                    <div className="mb-4">
                      <span className="text-5xl font-bold text-slate-800">4,99€</span>
                      <span className="text-slate-500 ml-2">TTC</span>
                    </div>
                    <p className="text-sm text-slate-500 mb-6">
                      par simulation
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                      <Shield className="h-4 w-4" />
                      <span>Paiement sécurisé</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 px-8 py-6 md:px-12">
                <p className="text-sm text-blue-800">
                  <strong>Parcours guidé par étapes :</strong> Remplissez chaque étape à votre rythme, modifiez vos choix à tout moment, puis générez votre dossier PDF complet. Chaque simulation donne droit à un téléchargement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Prêt à analyser votre investissement ?
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Créez votre compte gratuitement et commencez votre première simulation en moins de 5 minutes.
          </p>
          <Button
            onClick={() => { setShowLoginModal(true); setLoginMode('register'); }}
            data-testid="cta-button"
            size="lg"
            className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8"
          >
            Créer un compte gratuit
          </Button>
        </div>
      </section>

      {/* SEO Section - Guides et Ressources */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">
            Guides et Ressources
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Tout ce que vous devez savoir sur l'investissement immobilier locatif
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a 
              href="/guides/simulateur-rentabilite-locative" 
              className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group"
            >
              <Calculator className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 mb-2">
                Simulateur rentabilité locative
              </h3>
              <p className="text-sm text-slate-600">
                Comment calculer la rentabilité de votre investissement immobilier ? Guide complet.
              </p>
            </a>
            
            <a 
              href="/guides/calcul-rentabilite-locative-emprunt" 
              className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group"
            >
              <TrendingUp className="h-8 w-8 text-emerald-600 mb-4" />
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 mb-2">
                Calcul avec emprunt
              </h3>
              <p className="text-sm text-slate-600">
                L'effet de levier bancaire : comment emprunter pour maximiser votre rendement.
              </p>
            </a>
            
            <a 
              href="/guides/rentabilite-airbnb-vs-location" 
              className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group"
            >
              <BarChart3 className="h-8 w-8 text-rose-600 mb-4" />
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 mb-2">
                Airbnb vs Location longue durée
              </h3>
              <p className="text-sm text-slate-600">
                Quelle stratégie est la plus rentable pour votre bien ? Comparatif détaillé.
              </p>
            </a>
            
            <a 
              href="/guides/cash-flow-immobilier" 
              className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group"
            >
              <Lightbulb className="h-8 w-8 text-purple-600 mb-4" />
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 mb-2">
                Cash-flow immobilier
              </h3>
              <p className="text-sm text-slate-600">
                L'indicateur clé pour un investissement qui s'autofinance. Définition et calcul.
              </p>
            </a>
          </div>
          
          {/* SEO Keywords cloud */}
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-500 mb-4">Vous recherchez :</p>
            <div className="flex flex-wrap justify-center gap-2">
              <span className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 border border-slate-200">simulateur rentabilité locative gratuit</span>
              <span className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 border border-slate-200">calcul rentabilité investissement</span>
              <span className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 border border-slate-200">tableau calcul rentabilité</span>
              <span className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 border border-slate-200">simulateur rentabilité locative nette</span>
              <span className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 border border-slate-200">bonne rentabilité locative</span>
              <span className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 border border-slate-200">calcul rentabilité locative avec emprunt</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-400 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col gap-8">
            {/* Main footer content */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-3">
                <Building2 className="h-6 w-6 text-white" />
                <span className="text-white font-semibold">Achat Immo Gagnant</span>
              </div>
              <div className="flex gap-6 text-sm">
                <button 
                  onClick={() => setShowCGV(true)}
                  className="hover:text-white transition-colors"
                >
                  Conditions Générales de Vente
                </button>
                <button 
                  onClick={() => setShowCGU(true)}
                  className="hover:text-white transition-colors"
                >
                  Conditions Générales d'Utilisation
                </button>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="border-t border-slate-700 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-sm">
                Développement <strong className="text-white">LPP Soft</strong> - Jean-Baptiste Giraud
              </p>
              <p className="text-sm">
                © {new Date().getFullYear()} Achat Immo Gagnant. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* CGV Modal */}
      {showCGV && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800">Conditions Générales de Vente</h3>
                <button onClick={() => setShowCGV(false)} className="text-slate-400 hover:text-slate-600 text-2xl">×</button>
              </div>
              <div className="space-y-4 text-sm text-slate-700">
                <h4 className="font-semibold text-slate-900">Article 1 - Objet</h4>
                <p>Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre LPP Soft, représenté par Jean-Baptiste Giraud, et tout utilisateur du service Achat Immo Gagnant.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 2 - Services proposés</h4>
                <p>Achat Immo Gagnant propose un service de simulation et d'analyse de rentabilité immobilière comprenant :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Analyse de marché automatisée (données Airbnb et INSEE)</li>
                  <li>Simulation financière complète</li>
                  <li>Calcul de fiscalité personnalisé</li>
                  <li>Génération d'un dossier PDF professionnel pour la banque</li>
                </ul>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 3 - Tarifs</h4>
                <p>Le prix d'une simulation complète avec génération du dossier PDF est de <strong>4,99€ TTC</strong>. Ce tarif inclut l'accès à toutes les fonctionnalités de simulation et le téléchargement du rapport PDF.</p>
                <p>Les membres VIP bénéficient d'un accès gratuit et illimité aux services.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 4 - Paiement</h4>
                <p>Le paiement s'effectue par carte bancaire via notre partenaire de paiement sécurisé Stripe (et/ou PayPal). Le service est accessible immédiatement après validation du paiement.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 5 - Droit de rétractation</h4>
                <p>Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne peut être exercé pour les contenus numériques fournis immédiatement après l'achat avec l'accord exprès du consommateur.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 6 - Nature des simulations et limitation de responsabilité</h4>
                <p><strong>IMPORTANT :</strong> Les simulations et analyses fournies par Achat Immo Gagnant sont des <strong>estimations à titre purement informatif</strong>. Elles sont basées sur les données disponibles au moment de la simulation et les paramètres saisis par l'utilisateur.</p>
                <p><strong>Ces simulations ne constituent en aucun cas :</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Un conseil en investissement personnalisé</li>
                  <li>Une expertise immobilière opposable</li>
                  <li>Une garantie de rentabilité future</li>
                  <li>Un document contractuel engageant la responsabilité de LPP Soft</li>
                </ul>
                <p>L'utilisateur reconnaît que les résultats des simulations peuvent différer de la réalité et que LPP Soft <strong>ne saurait être tenu responsable</strong> des décisions d'investissement prises sur la base de ces simulations, ni des éventuelles pertes financières qui pourraient en résulter.</p>
                <p>Il est recommandé de consulter des professionnels qualifiés (notaire, expert-comptable, conseiller en gestion de patrimoine) avant tout engagement financier.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 7 - Données personnelles</h4>
                <p>Les données personnelles collectées sont traitées conformément au RGPD. Pour plus d'informations, consultez nos Conditions Générales d'Utilisation.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 8 - Contact</h4>
                <p>Pour toute question relative aux présentes CGV, vous pouvez nous contacter à l'adresse : contact@achat-immo-gagnant.fr</p>
                
                <p className="text-xs text-slate-500 mt-6">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CGU Modal */}
      {showCGU && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800">Conditions Générales d'Utilisation</h3>
                <button onClick={() => setShowCGU(false)} className="text-slate-400 hover:text-slate-600 text-2xl">×</button>
              </div>
              <div className="space-y-4 text-sm text-slate-700">
                <h4 className="font-semibold text-slate-900">Article 1 - Mentions légales</h4>
                <p>Le site Achat Immo Gagnant est édité par LPP Soft, représenté par Jean-Baptiste Giraud.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 2 - Accès au service</h4>
                <p>L'accès au service nécessite la création d'un compte utilisateur. L'utilisateur s'engage à fournir des informations exactes et à maintenir la confidentialité de ses identifiants de connexion.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 3 - Utilisation du service</h4>
                <p>L'utilisateur s'engage à utiliser le service conformément à sa destination et à ne pas :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Tenter d'accéder frauduleusement au système</li>
                  <li>Utiliser le service à des fins illicites</li>
                  <li>Reproduire ou redistribuer le contenu sans autorisation</li>
                </ul>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 4 - Nature du service et avertissement</h4>
                <p><strong>AVERTISSEMENT IMPORTANT :</strong></p>
                <p>Achat Immo Gagnant est un <strong>outil de simulation à titre informatif uniquement</strong>. Les résultats fournis :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Sont des estimations basées sur des données publiques et des hypothèses de marché</li>
                  <li><strong>Ne constituent pas une expertise immobilière opposable</strong></li>
                  <li><strong>Ne constituent pas un conseil en investissement</strong></li>
                  <li>Peuvent différer significativement des résultats réels</li>
                </ul>
                <p>L'utilisateur reconnaît expressément que :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Toute décision d'investissement relève de sa seule responsabilité</li>
                  <li>Il doit consulter des professionnels qualifiés (notaire, expert-comptable, conseiller en patrimoine) avant de s'engager</li>
                  <li>Les conditions de marché évoluent et peuvent rendre obsolètes les projections</li>
                  <li>LPP Soft décline toute responsabilité quant aux conséquences financières des décisions prises sur la base des simulations</li>
                </ul>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 5 - Propriété intellectuelle</h4>
                <p>L'ensemble des éléments du site (textes, graphismes, logiciels, etc.) sont la propriété exclusive de LPP Soft. Toute reproduction est interdite sans autorisation préalable.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 6 - Protection des données personnelles</h4>
                <p>Conformément au RGPD, nous collectons et traitons vos données personnelles dans le respect de votre vie privée :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>Données collectées :</strong> email, nom, prénom, données de simulation</li>
                  <li><strong>Finalité :</strong> fourniture du service, amélioration de l'expérience utilisateur</li>
                  <li><strong>Durée de conservation :</strong> 3 ans après la dernière utilisation</li>
                  <li><strong>Droits :</strong> accès, rectification, suppression, portabilité</li>
                </ul>
                <p>Pour exercer vos droits, contactez-nous à : rgpd@achat-immo-gagnant.fr</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 7 - Cookies</h4>
                <p>Le site utilise des cookies techniques nécessaires au fonctionnement du service (authentification, session). Aucun cookie publicitaire n'est utilisé.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 8 - Limitation de responsabilité</h4>
                <p>LPP Soft ne saurait être tenu responsable :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Des dommages directs ou indirects résultant de l'utilisation du service</li>
                  <li>De l'impossibilité d'accéder au service</li>
                  <li>De l'exactitude des données de marché utilisées dans les simulations</li>
                  <li>Des décisions d'investissement prises par l'utilisateur</li>
                  <li>Des pertes financières éventuelles liées à un investissement immobilier</li>
                </ul>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 9 - Modification des CGU</h4>
                <p>LPP Soft se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés de toute modification substantielle.</p>
                
                <h4 className="font-semibold text-slate-900 mt-4">Article 10 - Droit applicable</h4>
                <p>Les présentes CGU sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents.</p>
                
                <p className="text-xs text-slate-500 mt-6">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            data-testid="login-modal"
          >
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800">
                  {loginMode === 'standard' && 'Connexion'}
                  {loginMode === 'register' && 'Créer un compte'}
                </h3>
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="text-slate-400 hover:text-slate-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Mode Tabs - Only Connexion and Inscription */}
              <div className="flex mb-6 bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setLoginMode('standard')}
                  data-testid="tab-login"
                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${
                    loginMode === 'standard'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Connexion
                </button>
                <button
                  onClick={() => setLoginMode('register')}
                  data-testid="tab-register"
                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-all ${
                    loginMode === 'register'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Inscription
                </button>
              </div>

              {/* Info banner for VIP users */}
              {loginMode === 'standard' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <strong>Membres VIP :</strong> Connectez-vous avec votre email et mot de passe habituel pour un accès illimité.
                </div>
              )}

              {/* Standard Login Form */}
              {loginMode === 'standard' && (
                <div>
                  <form onSubmit={handleStandardLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email" className="text-slate-700 mb-2 block">
                        Email
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          placeholder="email@example.com"
                          className="pl-10 border-slate-300 focus:border-blue-500"
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="login-password" className="text-slate-700 mb-2 block">
                        Mot de passe
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type={showLoginPassword ? 'text' : 'password'}
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="••••••••"
                          className="pl-10 pr-10 border-slate-300 focus:border-blue-500"
                          required
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="login-submit-button"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5"
                    >
                      {loading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-slate-500">ou</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    data-testid="google-login-button"
                    className="w-full bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 py-5"
                  >
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continuer avec Google
                  </Button>
                </div>
              )}

              {/* Register Form */}
              {loginMode === 'register' && (
                <div>
                  <form onSubmit={handleRegister} noValidate className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="register-firstname" className="text-slate-700 mb-2 block text-sm">
                          Prénom <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="register-firstname"
                            type="text"
                            value={registerFirstName}
                            onChange={(e) => { setRegisterFirstName(e.target.value); setRegisterErrors(prev => ({...prev, firstName: null})); }}
                            placeholder="Jean"
                            className={`pl-10 border-slate-300 focus:border-blue-500 ${registerErrors.firstName ? 'border-red-500' : ''}`}
                            data-testid="register-firstname-input"
                          />
                        </div>
                        {registerErrors.firstName && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {registerErrors.firstName}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="register-name" className="text-slate-700 mb-2 block text-sm">
                          Nom <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="register-name"
                          type="text"
                          value={registerName}
                          onChange={(e) => { setRegisterName(e.target.value); setRegisterErrors(prev => ({...prev, name: null})); }}
                          placeholder="Dupont"
                          className={`border-slate-300 focus:border-blue-500 ${registerErrors.name ? 'border-red-500' : ''}`}
                          data-testid="register-name-input"
                        />
                        {registerErrors.name && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {registerErrors.name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-email" className="text-slate-700 mb-2 block text-sm">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          value={registerEmail}
                          onChange={(e) => { setRegisterEmail(e.target.value); setRegisterErrors(prev => ({...prev, email: null})); }}
                          placeholder="email@example.com"
                          className={`pl-10 border-slate-300 focus:border-blue-500 ${registerErrors.email ? 'border-red-500' : ''}`}
                          data-testid="register-email-input"
                        />
                      </div>
                      {registerErrors.email && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {registerErrors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="register-password" className="text-slate-700 mb-2 block text-sm">
                        Mot de passe <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-password"
                          type={showRegisterPassword ? 'text' : 'password'}
                          value={registerPassword}
                          onChange={(e) => { setRegisterPassword(e.target.value); setRegisterErrors(prev => ({...prev, password: null})); }}
                          placeholder="Min. 6 caractères"
                          className={`pl-10 pr-10 border-slate-300 focus:border-blue-500 ${registerErrors.password ? 'border-red-500' : ''}`}
                          data-testid="register-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {registerErrors.password && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {registerErrors.password}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="register-password-confirm" className="text-slate-700 mb-2 block text-sm">
                        Confirmer le mot de passe <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-password-confirm"
                          type={showRegisterPassword ? 'text' : 'password'}
                          value={registerPasswordConfirm}
                          onChange={(e) => { setRegisterPasswordConfirm(e.target.value); setRegisterErrors(prev => ({...prev, passwordConfirm: null})); }}
                          placeholder="Confirmez votre mot de passe"
                          className={`pl-10 border-slate-300 focus:border-blue-500 ${registerErrors.passwordConfirm ? 'border-red-500' : ''}`}
                          data-testid="register-password-confirm-input"
                        />
                      </div>
                      {registerErrors.passwordConfirm && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {registerErrors.passwordConfirm}
                        </p>
                      )}
                    </div>

                    {/* Referral Code Field */}
                    <div>
                      <Label className="text-slate-700 flex items-center gap-2">
                        <Gift className="h-4 w-4 text-purple-500" />
                        Code de parrainage <span className="text-slate-400 text-xs font-normal">(optionnel)</span>
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          type="text"
                          placeholder="Ex: ABC123XY"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                          className={`uppercase tracking-wider border-slate-300 focus:border-purple-500 ${
                            referralValid === true ? 'border-green-500 bg-green-50' : 
                            referralValid === false ? 'border-red-500 bg-red-50' : ''
                          }`}
                          data-testid="register-referral-input"
                        />
                      </div>
                      {referralValid === true && (
                        <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Code de {referralReferrer} valide ! +1 simulation gratuite
                        </p>
                      )}
                      {referralValid === false && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Code invalide
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="register-button"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5"
                    >
                      {loading ? 'Création...' : 'Créer mon compte'}
                    </Button>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-slate-500">ou</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    data-testid="google-register-button"
                    className="w-full bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 py-5"
                  >
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    S'inscrire avec Google
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* USP Popup - Dossier Bancaire */}
      {showUSPPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div 
            className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300"
            data-testid="usp-popup"
          >
            {/* Header avec gradient */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Votre Dossier Bancaire Complet</h3>
                  <p className="text-blue-100 text-sm">Généré automatiquement</p>
                </div>
              </div>
            </div>
            
            {/* Contenu */}
            <div className="p-6">
              <p className="text-slate-700 mb-4 leading-relaxed">
                <strong>Achat Immo Gagnant</strong> ne se contente pas de calculer votre rentabilité : 
                nous générons un <strong>dossier professionnel complet</strong> pour convaincre votre banquier 
                d'accorder un financement à <strong>100% voire 110%</strong>.
              </p>
              
              <div className="bg-blue-50 rounded-xl p-4 mb-4">
                <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Ce que contient votre dossier :
                </h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Analyse de marché avec données réelles (Airbnb + INSEE)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Simulation financière détaillée sur la durée du prêt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Argumentaire bancaire personnalisé pour défendre votre projet</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Stratégie de sortie et estimation de plus-value</span>
                  </li>
                </ul>
              </div>
              
              <p className="text-sm text-slate-500 mb-4">
                Tout ça pour seulement <strong className="text-blue-600">4,99€</strong> par simulation.
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => handleUSPPopupClose(false)}
                  variant="outline"
                  className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Plus tard
                </Button>
                <Button
                  onClick={() => handleUSPPopupClose(true)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="usp-cta-button"
                >
                  Commencer maintenant
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PWA Install Prompt - Shows AFTER USP popup is closed */}
      <PWAInstallPrompt 
        show={showPWAPrompt} 
        onClose={() => setShowPWAPrompt(false)} 
      />
    </div>
  );
}
